import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Context/auth';
import MenuLateral from '../../Components/MenuPrincipal/MenuLateral';
import './dashboard-vendas.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Swal from 'sweetalert2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement, // Register Line elements
    PointElement,
    Title,
    Tooltip,
    Legend
);

const DashboardVendas = () => {
    const { currentUser } = useAuth();

    const [data, setData] = useState({
        kpis: {
            qtdPedidos: 0,
            totalVendido: 0,
            totalItens: 0,
            ticketMedio: 0
        },
        evolution: [],
        topClientes: [],
        topProdutos: []
    });

    const [loading, setLoading] = useState(true);
    const [filterPeriod, setFilterPeriod] = useState('thisMonth');

    const fetchData = React.useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const sessionId = localStorage.getItem('sessionId');

            const API_URL = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${API_URL}/api/pedidos/dashboard?period=${filterPeriod}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-session-id': sessionId
                }
            });

            if (response.ok) {
                const result = await response.json();
                console.log("Dashboard Vendas Data:", result);
                setData(result);
            } else {
                const errorText = await response.text();
                console.error("Erro dashboard vendas:", response.status, errorText);
                Swal.fire('Erro', 'Não foi possível carregar os dados.', 'error');
            }
        } catch (error) {
            console.error("Erro fetch dashboard vendas:", error);
            Swal.fire('Erro', 'Falha na comunicação com o servidor.', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentUser, filterPeriod]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Formatters

    // Formatters
    const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
    const numberFormatter = new Intl.NumberFormat('pt-BR');

    // Chart Configuration (Evolução de Vendas)
    const evolutionChartData = {
        labels: data.evolution.map(d => d.label),
        datasets: [
            {
                type: 'bar',
                label: 'Total Vendido (R$)',
                data: data.evolution.map(d => d.total),
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
                yAxisID: 'y',
                order: 2
            },
            {
                type: 'line',
                label: 'Qtd. Pedidos',
                data: data.evolution.map(d => d.qtd),
                borderColor: 'rgba(255, 99, 132, 1)',
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                borderWidth: 2,
                pointRadius: 4,
                yAxisID: 'y1',
                order: 1
            }
        ]
    };

    const evolutionChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: {
                display: true,
                text: ['hoje', 'thisWeek'].includes(filterPeriod)
                    ? 'Evolução de Vendas (Diário)'
                    : 'Evolução de Vendas (Mensal - Ano Atual)'
            },
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: 'Valor (R$)' }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                grid: { drawOnChartArea: false }, // only want the grid lines for one axis to show up
                title: { display: true, text: 'Quantidade' }
            },
        },
    };


    if (loading) {
        return (
            <div className="dashboard-container" style={{ display: 'flex' }}>
                <MenuLateral />
                <div className="content-with-sidebar d-flex justify-content-center align-items-center" style={{ width: '100%', height: '100vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />
            <div className="content-with-sidebar" style={{ width: '100%' }}>
                <div className="container-fluid p-4">

                    {/* Header */}
                    <div className="vendas-header">
                        <h2 className="vendas-title"><i className="fas fa-chart-line me-2"></i>Dashboard de Vendas</h2>
                        <div className="d-flex gap-3 align-items-center">
                            <div className="d-flex align-items-center">
                                <label className="me-2 fw-bold text-secondary mb-0">Período:</label>
                                <div style={{ width: '180px' }}>
                                    <select
                                        className="form-select"
                                        value={filterPeriod}
                                        onChange={(e) => setFilterPeriod(e.target.value)}
                                    >
                                        <option value="hoje">Hoje</option>
                                        <option value="thisWeek">Esta Semana</option>
                                        <option value="thisMonth">Este Mês</option>
                                        <option value="thisYear">Este Ano</option>
                                    </select>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <button className="btn btn-outline-secondary btn-sm" onClick={fetchData} title="Atualizar">
                                    <i className="fas fa-sync-alt"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* KPIs Cards */}
                    <div className="row g-3 mb-4">
                        {/* Qtd Pedidos */}
                        <div className="col-lg-3 col-md-6">
                            <div className="card h-100 shadow-sm border-0 border-start border-4 border-primary">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1">Quantidade de pedidos</h6>
                                            <h3 className="fw-bold text-dark mb-0">{numberFormatter.format(data.kpis.qtdPedidos)}</h3>
                                        </div>
                                        <div className="p-2 rounded-circle bg-primary bg-opacity-10 text-primary">
                                            <i className="fas fa-shopping-cart fa-lg"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Total Vendido */}
                        <div className="col-lg-3 col-md-6">
                            <div className="card h-100 shadow-sm border-0 border-start border-4 border-success">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1">Total Vendido</h6>
                                            <h3 className="fw-bold text-success mb-0">{currencyFormatter.format(data.kpis.totalVendido)}</h3>
                                        </div>
                                        <div className="p-2 rounded-circle bg-success bg-opacity-10 text-success">
                                            <i className="fas fa-dollar-sign fa-lg"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ticket Médio */}
                        <div className="col-lg-3 col-md-6">
                            <div className="card h-100 shadow-sm border-0 border-start border-4 border-info">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1">Ticket Médio</h6>
                                            <h3 className="fw-bold text-dark mb-0">{currencyFormatter.format(data.kpis.ticketMedio)}</h3>
                                        </div>
                                        <div className="p-2 rounded-circle bg-info bg-opacity-10 text-info">
                                            <i className="fas fa-receipt fa-lg"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Total Produtos (Itens) */}
                        <div className="col-lg-3 col-md-6">
                            <div className="card h-100 shadow-sm border-0 border-start border-4 border-warning">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="text-secondary text-uppercase fw-bold mb-1">Produtos Vendidos</h6>
                                            <h3 className="fw-bold text-dark mb-0">{numberFormatter.format(data.kpis.totalItens)}</h3>
                                        </div>
                                        <div className="p-2 rounded-circle bg-warning bg-opacity-10 text-warning">
                                            <i className="fas fa-box-open fa-lg"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Graph: Evolution */}
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-body">
                            <div style={{ position: 'relative', height: '300px', width: '100%' }}>
                                <Bar data={evolutionChartData} options={evolutionChartOptions} />
                            </div>
                        </div>
                    </div>

                    {/* Rankings: Top Clientes & Top Produtos */}
                    <div className="row g-3">
                        {/* Top Clientes */}
                        <div className="col-lg-6">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-header bg-white border-0 py-3">
                                    <h6 className="mb-0 fw-bold text-primary"><i className="fas fa-users me-2"></i>Top 5 Clientes</h6>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-4">Cliente</th>
                                                    <th className="text-center">Qtd. Pedidos</th>
                                                    <th className="text-end pe-4">Total Comprado</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.topClientes.length > 0 ? (
                                                    data.topClientes.map((cliente, index) => (
                                                        <tr key={index}>
                                                            <td className="ps-4 fw-medium text-dark">{cliente.nome}</td>
                                                            <td className="text-center">{cliente.qtd_pedidos}</td>
                                                            <td className="text-end pe-4 fw-bold">{currencyFormatter.format(cliente.total_comprado)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="3" className="text-center py-3 text-muted">Nenhum dado disponível.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Top Produtos */}
                        <div className="col-lg-6">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-header bg-white border-0 py-3">
                                    <h6 className="mb-0 fw-bold text-success"><i className="fas fa-box me-2"></i>Top 5 Produtos</h6>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-4">Produto</th>
                                                    <th className="text-end pe-4">Qtd. Vendida</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.topProdutos.length > 0 ? (
                                                    data.topProdutos.map((produto, index) => (
                                                        <tr key={index}>
                                                            <td className="ps-4 fw-medium text-dark">{produto.descricao}</td>
                                                            <td className="text-end pe-4 fw-bold">{numberFormatter.format(produto.qtd_vendida)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="2" className="text-center py-3 text-muted">Nenhum dado disponível.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DashboardVendas;
